import downloadFile from "common/utils/downloadFile";
import { useEffect, useRef } from "react";
import Button from "ui/buttons/Button";
import Autosave from "ui/components/Autosave";
import cx from "ui/cx";
import IconAddLarge from "ui/icons/IconAddLarge";
import IconDownload from "ui/icons/IconDownload";
import SearchInput from "ui/search/SearchInput";
import { DATA_TABLE_CONTROLS_SELECTOR } from "./constants";
import styles from "./Controls.module.scss";
import type { ControlsProps, RowValueType } from "./types";

// TODO: Provide different layout options to fit various designs

export default function Controls<T extends {}>({
  title,
  children,
  autosave,
  addNew,
  addNewRow,
  setGlobalFilter,
  globalFilter,
  className,
  search,
  customControls,
  secondaryRow,
  downloadCsv,
  getTableData,
  tableRef,
}: ControlsProps<T> & {
  // internal props
  addNewRow: (emptyRow: Partial<T>) => void;
  setGlobalFilter: ((search: string) => void) | null;
  globalFilter: string;
  getTableData: {
    getColumnHeaders: () => string[];
    getProcessedRowValues: () => RowValueType[];
  };
  tableRef: React.RefObject<HTMLDivElement | null>;
}) {
  const controlsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = controlsRef.current;
    if (!element) {
      return;
    }

    const updateHeight = () => {
      const height = element.offsetHeight;
      tableRef.current?.style.setProperty("--table-controls-height", `${height}px`);
    };

    updateHeight();
    // Set up ResizeObserver for dynamic height changes
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div ref={controlsRef} className={cx(styles.controls, DATA_TABLE_CONTROLS_SELECTOR, className)} title={title}>
      <div className={styles.controlsRow}>
        <div className={styles.title}>
          {children || title}
          {search && setGlobalFilter && (
            <SearchInput
              size="medium"
              onChange={(e) => setGlobalFilter(e.currentTarget.value)}
              clearSearch={() => setGlobalFilter("")}
              value={globalFilter}
              placeholder={typeof search === "string" ? search : "Search"}
            />
          )}
        </div>
        <div className={styles.actions}>
          <Autosave autosave={autosave} />
          {customControls}
          {downloadCsv && (
            <Button
              btnSize="small"
              btnStyle="secondary"
              onClick={async () => {
                const { unparse } = await import("papaparse");
                const fields = (downloadCsv.formatHeaders ?? ((f) => f))(getTableData.getColumnHeaders());
                const data = (downloadCsv.formatData ?? ((f) => f))(getTableData.getProcessedRowValues());
                const csv = unparse({ fields, data }, { header: true });
                downloadFile(`${downloadCsv.filename}.csv`, csv, "text/csv");
              }}
              disabled={getTableData.getProcessedRowValues().length < 1}
            >
              <IconDownload />
              <span>Download .CSV</span>
            </Button>
          )}
          {addNew && (
            <Button btnSize="small" btnStyle="primary" onClick={() => addNewRow(addNew.emptyRow || {})}>
              <IconAddLarge size={16} />
              <span>{addNew.text ?? "New"}</span>
            </Button>
          )}
        </div>
      </div>
      {secondaryRow && <div className={styles.secondaryRow}>{secondaryRow}</div>}
    </div>
  );
}
